import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import ItemImage from './ItemImage';
import { LaneDot, ReasonChips } from './Tags';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { ErrorState } from '../common/States';
import { api } from '../../lib/api';
import { FOCUS_OPTIONS } from '../../lib/taxonomy';
import { fmtInt, fmtPrice, variantLabel } from '../../lib/format';

export function MiniItem({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className="group flex flex-col overflow-hidden rounded-lg border bg-card text-left transition hover:border-foreground/20"
    >
      <ItemImage src={item.image_url} alt={item.title} className="aspect-square w-full border-b p-2" rounded="rounded-none" />
      <div className="flex flex-1 flex-col gap-1 p-2">
        <div className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">{item.brand}</div>
        <div className="line-clamp-2 text-xs font-medium leading-snug group-hover:underline">{item.title}</div>
        {variantLabel(item.variant_title) && <div className="truncate text-[11px] text-muted-foreground">{variantLabel(item.variant_title)}</div>}
        <div className="flex items-center gap-1.5 text-[11px] tabular-nums text-muted-foreground">
          <LaneDot lane={item.exit_lane} />
          <span>{fmtInt(item.free_qty)} pcs</span>
          <span>·</span>
          <span>{fmtPrice(item.price)}</span>
          {item.visual_sim != null && <span className="ml-auto text-sky-700 dark:text-sky-300">{Math.round(item.visual_sim * 100)}</span>}
        </div>
        <ReasonChips reasons={item.match_reasons} max={3} className="mt-auto pt-1" />
      </div>
    </button>
  );
}

export default function SimilarItems({ odooId, onOpen }) {
  const [focus, setFocus] = useState('balanced');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['similar', odooId, focus],
    queryFn: () => api.similar(odooId, focus),
    enabled: !!odooId,
  });
  const results = data?.results || [];
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-muted-foreground" /> Similar problem stock
        </h3>
        <Tabs value={focus} onValueChange={setFocus}>
          <TabsList className="h-8">
            {FOCUS_OPTIONS.map((f) => (
              <TabsTrigger key={f.value} value={f.value} className="px-2.5 py-0.5 text-xs">
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      {error && <ErrorState error={error} compact onRetry={refetch} title="Could not load similar items" />}
      {isLoading && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4]" />
          ))}
        </div>
      )}
      {!isLoading && !error && !results.length && (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">No similar problem stock found for this focus.</p>
      )}
      {!!results.length && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {results.map((r) => (
            <MiniItem key={r.odoo_product_id} item={r} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}
